import React, { useCallback, useMemo, useState } from "react";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import DataTable from "@/components/Datatable/Datatable";
import { useMyEmailNotifications } from "@/features/myEmailNotifications/hooks/useMyEmailNotifications";
import { createMyEmailNotificationsColumns } from "@/features/myEmailNotifications/components/MyEmailNotificationsColumns/MyEmailNotificationsColumns";
import MyEmailNotificationsFilters from "@/features/myEmailNotifications/components/MyEmailNotificationsFilters/MyEmailNotificationsFilters";
import MyEmailNotificationPreview from "@/features/myEmailNotifications/components/MyEmailNotificationPreview/MyEmailNotificationPreview";
import { EmailNotification } from "@/api/fetchMyEmailNotifications/types";

function MyEmailNotificationsList() {
  const {
    notifications,
    totalCount,
    loading,
    error,
    pagination,
    sorting,
    filters,
    handlePaginationChange,
    handleSortingChange,
    handleFilterChange,
  } = useMyEmailNotifications();

  // Preview modal state
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [notificationToPreview, setNotificationToPreview] =
    useState<EmailNotification | null>(null);

  // Preview handler
  const handleAskPreview = useCallback((notification: EmailNotification) => {
    setNotificationToPreview(notification);
    setPreviewModalOpen(true);
  }, []);

  const handlePreviewClose = useCallback(() => {
    setPreviewModalOpen(false);
    setNotificationToPreview(null);
  }, []);

  // Build columns with our callbacks
  const columns = useMemo(
    () =>
      createMyEmailNotificationsColumns({
        onAskPreview: handleAskPreview,
      }),
    [handleAskPreview],
  );

  return (
    <MainPageWrapper error={error} title="My Email Notifications">
      <div className="mb-6">
        <p className="text-gray-600">
          View all your chapter email notifications and their current status.
        </p>
      </div>

      <MyEmailNotificationsFilters
        filters={filters}
        onFilterChange={handleFilterChange}
      />

      <DataTable
        columns={columns}
        data={notifications}
        error={error}
        loading={loading}
        noDataMessage="You haven't created any email notifications yet."
        onPageChange={(pageIndex, pageSize) =>
          handlePaginationChange({
            pageIndex,
            pageSize,
          })
        }
        onSortingChange={handleSortingChange}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        rowKeyExtractor={(item) => String(item.id)}
        sorting={sorting}
        totalCount={totalCount}
      />

      {/* Preview Modal */}
      <MyEmailNotificationPreview
        isOpen={previewModalOpen}
        notification={notificationToPreview}
        onClose={handlePreviewClose}
      />
    </MainPageWrapper>
  );
}

export default MyEmailNotificationsList;
